import { Box, Text, ToggleButton } from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import { StatusWarning, StatusWarningFilled } from '@wix/wix-ui-icons-common';
import React, { FC, useCallback } from 'react';
import { useStore } from 'react-redux';

import { TLocalesKeys } from '../../../../types';
import { ButtonClicked } from '../../../bi';
import { logArticleVoteButtonClick } from '../../../bi/loggers';
import { HelpCenterVoteableArticle } from '../../types';
import { articleVoteButtonDataHooks } from './ArticleVoteButton.dataHooks';

export interface KnownIssueVoteButtonProps {
  article: HelpCenterVoteableArticle;
  onVote: () => void;
  isVoted: boolean;
  isVotedInCurrentSession: boolean;
}

export const KnownIssueVoteButton: FC<KnownIssueVoteButtonProps> = ({
  article,
  onVote,
  isVoted,
  isVotedInCurrentSession,
}) => {
  const store = useStore();
  const { t } = useTranslation();

  const onClick = useCallback(() => {
    const newVotedValue = !isVoted;

    const buttonClicked = newVotedValue ? ButtonClicked.KnownIssueVote : ButtonClicked.KnownIssueUnvote;

    logArticleVoteButtonClick(store.getState(), {
      articleId: article.id,
      buttonClicked,
    });

    onVote();
  }, [article, onVote, isVoted, store]);

  return (
    <Box direction="vertical" gap="SP2">
      <Box>
        <ToggleButton
          shape="pill"
          border
          selected={isVoted}
          onClick={onClick}
          labelValue={
            // Have to use span because ToggleButton driver has an issue with .click()
            <span data-hook={articleVoteButtonDataHooks.knownIssueVoteButton()}>
              {isVoted
                ? t<TLocalesKeys>('article_footer_unfollow_known_issue_btn')
                : t<TLocalesKeys>('article_footer_follow_known_issue_btn')}
            </span>
          }
        >
          {isVoted ? <StatusWarningFilled /> : <StatusWarning />}
        </ToggleButton>
      </Box>
      {isVotedInCurrentSession ? (
        <Text weight="normal" size="small">
          {t<TLocalesKeys>('article_footer_known_issue_thanks_text')}
        </Text>
      ) : null}
    </Box>
  );
};
